import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { resolveInvite } from "../hooks/useInvites";
import AuthModal from "../components/auth/AuthModal";
import "../styles/landing.css";

/**
 * InviteAcceptPage — /invite/:token
 *
 * Resolves the invite token from Firestore, shows context ("You've been
 * invited to help track [childName] by [ownerEmail]"), then presents a
 * sign-up / log-in form with the caregiver's email pre-filled.
 *
 * After successful auth, React Router navigates to /app. ChildContext's
 * caregiver query picks up the child automatically — no extra wiring needed.
 */
export default function InviteAcceptPage() {
  const { token }         = useParams();
  const navigate          = useNavigate();
  const [invite, setInvite]   = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [resolving, setResolving] = useState(true);
  const [authOpen, setAuthOpen]   = useState(false);
  const [authMode, setAuthMode]   = useState("signup");

  useEffect(() => {
    resolveInvite(token)
      .then((data) => {
        if (data) setInvite(data);
        else      setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setResolving(false));
  }, [token]);

  const handleAccept = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  if (resolving) {
    return (
      <div className="invite-page">
        <div className="invite-card">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="invite-page">
        <div className="invite-card">
          <div className="invite-icon">❌</div>
          <h1 className="invite-heading">Invite not found</h1>
          <p className="invite-sub">
            This invite link is invalid or has already been used.
            Ask the person who invited you to send a new link.
          </p>
          <a href="/" className="btn btn-outline-secondary">Go to home page</a>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="invite-card">
        <div className="invite-icon">👶</div>
        <h1 className="invite-heading">You've been invited</h1>
        <p className="invite-sub">
          <strong>{invite.ownerEmail}</strong> has invited you to help track{" "}
          <strong>{invite.childName}</strong>.
        </p>
        <p className="invite-desc">
          Create a free account (or log in if you already have one) to access
          the full dashboard for {invite.childName}.
        </p>

        <div className="invite-actions">
          <button className="btn btn-primary btn-lg" onClick={() => handleAccept("signup")}>
            Create account &amp; accept
          </button>
          <button className="btn btn-outline-secondary" onClick={() => handleAccept("login")}>
            I already have an account — log in
          </button>
        </div>

        <p className="invite-email-hint">
          Your invite is for <strong>{invite.caregiverEmail}</strong>. Use that
          email when signing up or logging in.
        </p>
      </div>

      {authOpen && (
        <AuthModal
          initialMode={authMode}
          prefillEmail={invite.caregiverEmail}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); navigate("/app"); }}
        />
      )}
    </div>
  );
}
